import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CHARACTERS,
  DENSITIES,
  DENSITY_LABELS,
  MAX_CHARACTERS,
  PACKAGE_OPTIONS,
  applyCallDefaults,
  callSheetPrintGaps,
  characterChips,
  contrastRatio,
  customerServiceTitles,
  demoFilledFields,
  densityFromLegacyPersonality,
  formatCallSheetGaps,
  isStarkHex,
  lookPresets,
  pickOnAccentColor,
  resolveLook,
  sitemapCrumbs,
  validateMultiTonePalette,
  type Character,
  type Density,
  type LeadRecord,
  type PageCount,
  type PlaceholderOverlayOpts,
  type Service,
} from '@flashpoint/site-dna';
import { useLocalText, useLocalTextWithCallback } from '../../wizard/useLocalText';
import { useLocalCommaList, useLocalDraftField } from '../../wizard/useLocalDraftField';
import { useLeadDraft } from '../../lib/useLeadDraft';
import { mergeSiteDnaAssets, patchLead, updateLeadStatus } from '../../lib/firestore';
import { uploadLeadAsset } from '../../lib/storage';
import { provisionLeadStream, lockLeadCode, type ProvisionStreamEvent } from '../../lib/orchestratorClient';
import {
  DeployPreviewStatus,
  ProvisionStreamLine,
  deriveDeployPhase,
  lastCloudflareHeadSha,
} from '../../components/DeployPreviewStatus';
import { useAuth } from '../../auth/AuthContext';
import { logHistory } from '../../lib/historyLog';
import { AssetSlot, GallerySlot } from '../../components/AssetSlot';
import { IndustryPicker, fieldInputClass, type FieldTone } from '../../components/IndustryPicker';
import { PreviewLink } from './PreviewLink';

const CUES = {
  who: 'How do people around here find you?',
  hire: 'Walk me through a typical job — what do you actually do for them?',
  reach: 'If someone wanted to get on the calendar today, what should they do?',
  truck: 'If you had one sentence on the side of the truck, what would it say?',
  proof: 'Tell me about a job you were proud of.',
} as const;

type BeatState = 'done' | 'demo' | 'open';

interface BeatStatus {
  state: BeatState;
  label: string;
}

/** Any demo copy taints the whole beat amber; done only when the required set is owned. */
function beatStatusFor(tones: FieldTone[], done: boolean, openLabel: string): BeatStatus {
  if (tones.includes('demo')) return { state: 'demo', label: 'Demo' };
  if (done) return { state: 'done', label: 'Done' };
  return { state: 'open', label: openLabel };
}

function beatCardClass(state: BeatState): string {
  if (state === 'done') return 'fp-beat-done';
  if (state === 'demo') return 'fp-beat-demo';
  return '';
}

function servicesEqual(a: Service[], b: Service[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

interface Props {
  lead: LeadRecord;
}

export function CallSheet({ lead }: Props) {
  const { user } = useAuth();
  const draft = useLeadDraft(lead.id, { debounceMs: 900 });
  const b = lead.siteDna.businessInfo;
  const tokens = lead.siteDna.designTokens;
  const industry = lead.context.industryVertical || b.industryVertical || '';
  const personality = tokens.personality ?? [];
  const characters = characterChips(personality);
  const look = resolveLook(industry, personality, tokens.lookVariant ?? 0, tokens.density);

  const [name, setName] = useLocalText(lead.id, b.name, 'siteDna.businessInfo.name', draft.queueField);
  const [headline, setHeadline] = useLocalText(lead.id, b.headline, 'siteDna.businessInfo.headline', draft.queueField);
  const [ctaLabel, setCta] = useLocalText(lead.id, b.ctaLabel, 'siteDna.businessInfo.ctaLabel', draft.queueField);
  const [phone, setPhone] = useLocalText(lead.id, b.phone, 'siteDna.businessInfo.phone', draft.queueField);
  const [email, setEmail] = useLocalText(lead.id, b.email, 'siteDna.businessInfo.email', draft.queueField);
  const [area, setArea] = useLocalText(lead.id, b.serviceArea, 'siteDna.businessInfo.serviceArea', draft.queueField);
  const [industryLocal, setIndustryLocal] = useLocalText(
    lead.id,
    industry,
    'context.industryVertical',
    draft.queueField,
  );

  const presets = lookPresets(industryLocal || industry);
  const presetCount = presets.length || 1;
  const activePresetIndex = (((tokens.lookVariant ?? 0) % presetCount) + presetCount) % presetCount;

  const [services, setServices, resetServices] = useLocalDraftField<Service[]>(
    lead.id,
    b.services ?? [],
    'siteDna.businessInfo.services',
    draft.queueField,
    servicesEqual,
  );
  const [chips, setChips, resetChips] = useLocalCommaList(
    lead.id,
    b.proofChips,
    'siteDna.businessInfo.proofChips',
    draft.queueField,
  );

  const queueTestimonials = useCallback(
    (nextQuote: string, nextName: string) => {
      const rest = (b.testimonials ?? []).slice(1);
      if (!nextQuote.trim()) {
        draft.queueField('siteDna.businessInfo.testimonials', rest);
        return;
      }
      const prev = b.testimonials?.[0];
      draft.queueField('siteDna.businessInfo.testimonials', [
        {
          quote: nextQuote.trim(),
          name: nextName.trim() || prev?.name || 'Client',
          role: prev?.role,
          initials: prev?.initials,
        },
        ...rest,
      ]);
    },
    [b.testimonials, draft.queueField],
  );

  const quoteRef = useRef(b.testimonials?.[0]?.quote ?? '');
  const quoteNameRef = useRef(b.testimonials?.[0]?.name ?? '');

  const queueBothTestimonials = useCallback(
    (nextQuote: string, nextName: string) => {
      quoteRef.current = nextQuote;
      quoteNameRef.current = nextName;
      queueTestimonials(nextQuote, nextName);
    },
    [queueTestimonials],
  );

  const [quote, setQuote, resetQuote] = useLocalTextWithCallback(
    lead.id,
    b.testimonials?.[0]?.quote ?? '',
    (nextQuote) => queueBothTestimonials(nextQuote, quoteNameRef.current),
  );
  const [quoteName, setQuoteName, resetQuoteName] = useLocalTextWithCallback(
    lead.id,
    b.testimonials?.[0]?.name ?? '',
    (nextName) => queueBothTestimonials(quoteRef.current, nextName),
  );
  quoteRef.current = quote;
  quoteNameRef.current = quoteName;

  const queueAccent = useCallback(
    (nextAccent: string) => {
      draft.queueField('siteDna.designTokens.multiTonePalette', {
        ...tokens.multiTonePalette,
        accent: nextAccent,
      });
    },
    [draft.queueField, tokens.multiTonePalette],
  );
  const [accent, setAccent, resetAccent] = useLocalTextWithCallback(
    lead.id,
    tokens.multiTonePalette.accent,
    queueAccent,
  );
  const [accentIssue, setAccentIssue] = useState<string | null>(null);

  const livePalette = useMemo(
    () => ({ ...tokens.multiTonePalette, accent }),
    [tokens.multiTonePalette, accent],
  );
  const paletteIssues = useMemo(() => validateMultiTonePalette(livePalette), [livePalette]);
  const onAccentColor = useMemo(() => pickOnAccentColor(accent), [accent]);
  const accentContrast = useMemo(() => contrastRatio(onAccentColor, accent), [onAccentColor, accent]);

  const onAccentChange = (next: string) => {
    if (isStarkHex(next)) {
      setAccentIssue('Pure white and pure black are not allowed. Use an off-white or deep slate instead.');
      return;
    }
    setAccentIssue(null);
    setAccent(next);
  };

  const [running, setRunning] = useState(false);
  const [streamLog, setStreamLog] = useState<ProvisionStreamEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const deployPhase = useMemo(() => deriveDeployPhase(streamLog, running, err), [streamLog, running, err]);
  const previewCacheBust = useMemo(
    () => (deployPhase === 'live' ? lastCloudflareHeadSha(streamLog) : undefined),
    [deployPhase, streamLog],
  );
  const [showCues, setShowCues] = useState(true);
  const [lookOpen, setLookOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [assetBusy, setAssetBusy] = useState<string | null>(null);

  const proofChips = useMemo(
    () =>
      chips
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [chips],
  );

  const liveTestimonials = useMemo(() => {
    if (!quote.trim()) return b.testimonials;
    const prev = b.testimonials?.[0];
    return [
      {
        quote: quote.trim(),
        name: quoteName.trim() || prev?.name || 'Client',
        role: prev?.role,
        initials: prev?.initials,
      },
      ...(b.testimonials ?? []).slice(1),
    ];
  }, [quote, quoteName, b.testimonials]);

  const formInfo = useMemo(
    () => ({
      ...b,
      name,
      headline,
      ctaLabel,
      phone,
      email,
      serviceArea: area,
      industryVertical: industryLocal,
      services,
      proofChips,
      testimonials: liveTestimonials,
    }),
    [b, name, headline, ctaLabel, phone, email, area, industryLocal, services, proofChips, liveTestimonials],
  );

  const demo = useMemo(() => demoFilledFields(formInfo, industryLocal), [formInfo, industryLocal]);
  const customServiceTitles = useMemo(
    () => customerServiceTitles(formInfo, industryLocal),
    [formInfo, industryLocal],
  );
  const gaps = useMemo(() => callSheetPrintGaps(formInfo, industryLocal), [formInfo, industryLocal]);
  const gapLabel = useMemo(() => formatCallSheetGaps(gaps), [gaps]);
  const fromPipeline = Boolean(lead.logicBrooksLeadId);

  const serviceRows = useMemo(() => {
    const rows = [...services];
    while (rows.length < 3) rows.push({ title: '', description: '' });
    return rows;
  }, [services]);

  const withForm = (patch?: {
    designTokens?: LeadRecord['siteDna']['designTokens'];
    pageCount?: PageCount;
    industry?: string;
  }): LeadRecord => {
    const nextIndustry = patch?.industry ?? industryLocal;
    return {
      ...lead,
      context: { ...lead.context, industryVertical: nextIndustry },
      siteDna: {
        ...lead.siteDna,
        layout: patch?.pageCount
          ? { ...lead.siteDna.layout, pageCount: patch.pageCount }
          : lead.siteDna.layout,
        businessInfo: { ...lead.siteDna.businessInfo, ...formInfo, industryVertical: nextIndustry },
        designTokens: patch?.designTokens ?? {
          ...lead.siteDna.designTokens,
          multiTonePalette: { ...tokens.multiTonePalette, accent },
        },
      },
    };
  };

  const persistLead = async (next: LeadRecord) => {
    await draft.flush();
    await patchLead(lead.id, {
      context: next.context,
      siteDna: next.siteDna,
    });
  };

  const applyLook = async (
    patch: Partial<LeadRecord['siteDna']['designTokens']> & { industry?: string },
    overlay?: PlaceholderOverlayOpts,
  ) => {
    const nextIndustry = patch.industry ?? industryLocal;
    const next = applyCallDefaults(
      withForm({
        industry: nextIndustry,
        designTokens: {
          ...lead.siteDna.designTokens,
          ...patch,
          personality: patch.personality ?? personality,
          lookVariant: patch.lookVariant ?? tokens.lookVariant ?? 0,
          multiTonePalette: { ...tokens.multiTonePalette, accent },
        },
      }),
      { forceSections: true, copy: 'none', ...overlay },
    );
    await persistLead(next);
  };

  const onIndustryCommit = (next: string) => {
    void applyLook({ industry: next }, { copy: 'none' });
    draft.queueField('siteDna.businessInfo.industryVertical', next);
  };

  /**
   * Character chips are additive modifiers on the current preset — toggling
   * one never switches the preset. Legacy Quiet/Bold chips are migrated into
   * the density knob the first time the row is touched.
   */
  const toggleCharacter = (c: Character) => {
    const set = new Set(characters);
    if (set.has(c)) set.delete(c);
    else {
      if (set.size >= MAX_CHARACTERS) return;
      set.add(c);
    }
    const legacyDensity = tokens.density ?? densityFromLegacyPersonality(personality);
    void applyLook({
      personality: Array.from(set),
      ...(legacyDensity ? { density: legacyDensity } : {}),
    });
  };

  const setDensity = (d: Density) => {
    void applyLook({ density: d });
  };

  const selectPreset = (index: number) => {
    void applyLook({ lookVariant: index });
  };

  const applyPackage = async (pageCount: PageCount) => {
    const next = applyCallDefaults(withForm({ pageCount }), { forceSections: true, copy: 'none' });
    await persistLead(next);
  };

  const fillDemo = async () => {
    const next = applyCallDefaults(withForm(), {
      forceSections: true,
      copy: 'replaceDemo',
      previousIndustry: industry,
    });
    await persistLead(next);
    resetServices(next.siteDna.businessInfo.services ?? []);
    resetChips(next.siteDna.businessInfo.proofChips ?? []);
    resetQuote(next.siteDna.businessInfo.testimonials?.[0]?.quote ?? '');
    resetQuoteName(next.siteDna.businessInfo.testimonials?.[0]?.name ?? '');
    resetAccent(next.siteDna.designTokens.multiTonePalette.accent);
  };

  const updateService = (i: number, patch: Partial<Service>) => {
    const next =
      services.length > i
        ? [...services]
        : [
            ...services,
            ...Array.from({ length: i - services.length + 1 }, () => ({ title: '', description: '' })),
          ];
    next[i] = { ...next[i], ...patch };
    setServices(next);
    draft.queueField('siteDna.businessInfo.services', next);
  };

  const addService = () => {
    const next = [...services, { title: '', description: '' }];
    setServices(next);
  };

  const generate = async () => {
    if (lead.provisioning.codeLocked) {
      setErr('This project is locked. Flashpoint will not push to the GitHub repo.');
      return;
    }
    if (!name.trim() || !industryLocal.trim()) {
      setErr('Business name and industry are required.');
      return;
    }
    if (paletteIssues.length > 0) {
      setErr(paletteIssues[0] ?? 'Fix the accent color before generating.');
      return;
    }
    setErr(null);
    setStreamLog([]);
    setRunning(true);
    try {
      await draft.flush();
      if (draft.hasPending()) {
        throw new Error('Some call sheet changes did not save. Try again in a moment.');
      }
      await provisionLeadStream(lead.id, (ev) => {
        setStreamLog((prev) => [...prev.slice(-80), ev]);
      });
      await updateLeadStatus(lead.id, 'Demo Generated');
      logHistory({
        leadId: lead.id,
        user,
        kind: 'status_change',
        payload: { from: lead.leadStatus, to: 'Demo Generated', via: 'generate_preview' },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const uploadSlot = async (slot: 'logo' | 'hero' | 'owner' | 'gallery', file: File) => {
    setAssetBusy(slot);
    setErr(null);
    try {
      await draft.flush();
      const handle = uploadLeadAsset(lead.id, file, { slot });
      const ref = await handle.promise;
      if (slot === 'gallery') {
        await mergeSiteDnaAssets(lead.id, (prev) => ({
          ...prev,
          gallery: [...(prev.gallery ?? []), ref],
        }));
      } else {
        await mergeSiteDnaAssets(lead.id, (prev) => ({ ...prev, [slot]: ref }));
      }
      logHistory({
        leadId: lead.id,
        user,
        kind: 'asset_upload',
        payload: { slot, bytes: ref.bytes, mimeType: ref.mimeType, downloadURL: ref.downloadURL },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAssetBusy(null);
    }
  };

  const clearSlot = async (slot: 'logo' | 'hero' | 'owner') => {
    try {
      await draft.flush();
      await mergeSiteDnaAssets(lead.id, (prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const removeGalleryAt = async (i: number) => {
    try {
      await draft.flush();
      await mergeSiteDnaAssets(lead.id, (prev) => ({
        ...prev,
        gallery: (prev.gallery ?? []).filter((_, idx) => idx !== i),
      }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const locked = Boolean(lead.provisioning.codeLocked);
  const canGenerate =
    Boolean(name.trim() && industryLocal.trim()) &&
    !running &&
    !locked &&
    paletteIssues.length === 0 &&
    !isStarkHex(accent);
  const pageCount = lead.siteDna.layout.pageCount ?? 1;
  const crumbs = sitemapCrumbs(lead.siteDna.layout);
  const assets = lead.siteDna.assets;
  const photoCount =
    (assets.logo ? 1 : 0) + (assets.hero ? 1 : 0) + (assets.owner ? 1 : 0) + (assets.gallery?.length ?? 0);

  const nameTone = toneFor(name, false);
  const industryTone = toneFor(industryLocal, false);
  const areaTone = toneFor(area, demo.has('serviceArea'));
  const phoneTone = toneFor(phone, demo.has('phone'));
  const emailTone = toneFor(email, demo.has('email'));
  const ctaTone = toneFor(ctaLabel, demo.has('ctaLabel'));
  const headlineTone = toneFor(headline, demo.has('headline'));
  const chipsTone = toneFor(chips, demo.has('proofChips'), true);
  const quoteTone = toneFor(quote, demo.has('testimonial'), true);

  const serviceTitleTones: FieldTone[] = serviceRows.map((svc) => {
    const title = (svc.title ?? '').trim();
    if (!title) return 'empty';
    return customServiceTitles.includes(title) ? 'owned' : 'demo';
  });

  const whoTones = [nameTone, industryTone, areaTone];
  const whoStatus = beatStatusFor(
    whoTones,
    whoTones.every((t) => t === 'owned'),
    `${whoTones.filter((t) => t !== 'owned').length} to go`,
  );
  const ownedTitleCount = Math.min(customServiceTitles.length, 3);
  const hireStatus = beatStatusFor(serviceTitleTones, ownedTitleCount >= 3, `${3 - ownedTitleCount} to go`);
  const reachStatus = beatStatusFor(
    [phoneTone, emailTone, ctaTone],
    phoneTone === 'owned' || emailTone === 'owned',
    '1 to go',
  );
  const truckStatus = beatStatusFor([headlineTone], headlineTone === 'owned', '1 to go');
  const proofStatus = beatStatusFor(
    [chipsTone, quoteTone],
    chipsTone === 'owned' || quoteTone === 'owned',
    'Optional',
  );
  const photosStatus: BeatStatus =
    photoCount > 0 ? { state: 'done', label: `${photoCount} added` } : { state: 'open', label: 'Optional' };

  const scoreboard: Array<{ id: string; name: string; status: BeatStatus; onJump?: () => void }> = [
    { id: 'beat-who', name: 'Who', status: whoStatus },
    { id: 'beat-services', name: 'Services', status: hireStatus },
    { id: 'beat-reach', name: 'Reach', status: reachStatus },
    { id: 'beat-line', name: 'Line', status: truckStatus },
    { id: 'beat-proof', name: 'Proof', status: proofStatus, onJump: () => setProofOpen(true) },
    { id: 'beat-photos', name: 'Photos', status: photosStatus, onJump: () => setPhotosOpen(true) },
  ];

  const jumpTo = (beat: { id: string; onJump?: () => void }) => {
    beat.onJump?.();
    requestAnimationFrame(() => {
      document.getElementById(beat.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="space-y-4 pb-32">
      <div className="sticky top-0 z-10 -mx-1 space-y-2 bg-nm-bg px-1 py-2">
        <div className="flex items-center justify-between gap-3">
          <p className="fp-label">Call checklist</p>
          <button type="button" onClick={() => setShowCues((v) => !v)} className="fp-btn fp-btn-ghost !py-1.5">
            {showCues ? 'Hide prompts' : 'Show prompts'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {scoreboard.map((beat) => (
            <button
              key={beat.id}
              type="button"
              onClick={() => jumpTo(beat)}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors ${
                beat.status.state === 'done'
                  ? 'border-nm-success/50 bg-nm-success-bg text-nm-success'
                  : beat.status.state === 'demo'
                    ? 'border-nm-warn/50 bg-nm-warn-bg text-nm-warn'
                    : 'border-nm-border text-nm-muted hover:text-nm-ink'
              }`}
            >
              <span
                aria-hidden
                className={`h-2 w-2 rounded-full ${
                  beat.status.state === 'done'
                    ? 'bg-nm-success'
                    : beat.status.state === 'demo'
                      ? 'bg-nm-warn'
                      : 'bg-nm-tertiary'
                }`}
              />
              {beat.name}
            </button>
          ))}
        </div>
      </div>

      <BeatRow id="beat-who" title="Who they are" cue={CUES.who} showCue={showCues} status={whoStatus}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Business name" required demo={nameTone === 'demo'} owned={nameTone === 'owned'}>
            <input value={name} onChange={(e) => setName(e.target.value)} className={fieldInputClass(nameTone)} />
          </Field>
          <Field label="Industry" required owned={industryTone === 'owned'}>
            <IndustryPicker
              value={industryLocal}
              onChange={setIndustryLocal}
              onCommit={onIndustryCommit}
              tone={industryTone}
            />
          </Field>
          <Field label="City / service area" demo={areaTone === 'demo'} owned={areaTone === 'owned'} className="md:col-span-2">
            <input value={area} onChange={(e) => setArea(e.target.value)} className={fieldInputClass(areaTone)} />
          </Field>
        </div>
      </BeatRow>

      <BeatRow
        id="beat-services"
        title="What they hire you for"
        cue={CUES.hire}
        showCue={showCues}
        status={hireStatus}
        actions={
          <button type="button" onClick={addService} className="text-xs text-nm-accent hover:underline">
            + Add
          </button>
        }
      >
        <div className="space-y-3">
          {serviceRows.map((svc, i) => {
            const titleTone = serviceTitleTones[i] ?? 'empty';
            return (
              <div key={i} className="rounded-[12px] border border-nm-border bg-nm-surface p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    value={svc.title ?? ''}
                    onChange={(e) => updateService(i, { title: e.target.value })}
                    placeholder="Title"
                    className={fieldInputClass(titleTone)}
                  />
                  <input
                    value={svc.priceLabel ?? ''}
                    onChange={(e) => updateService(i, { priceLabel: e.target.value })}
                    placeholder="Price (optional)"
                    className="fp-input"
                  />
                </div>
                <textarea
                  value={svc.description ?? ''}
                  onChange={(e) => updateService(i, { description: e.target.value })}
                  rows={2}
                  placeholder="Short description"
                  className="fp-input mt-2"
                />
              </div>
            );
          })}
        </div>
      </BeatRow>

      <BeatRow id="beat-reach" title="How they want to be reached" cue={CUES.reach} showCue={showCues} status={reachStatus}>
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label="Phone"
            demo={phoneTone === 'demo'}
            owned={phoneTone === 'owned'}
            hint={fromPipeline && phone.trim() && phoneTone === 'owned' ? 'From pipeline' : undefined}
          >
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={fieldInputClass(phoneTone)} />
          </Field>
          <Field
            label="Email"
            demo={emailTone === 'demo'}
            owned={emailTone === 'owned'}
            hint={fromPipeline && email.trim() && emailTone === 'owned' ? 'From pipeline' : undefined}
          >
            <input value={email} onChange={(e) => setEmail(e.target.value)} className={fieldInputClass(emailTone)} />
          </Field>
          <Field label="CTA button" demo={ctaTone === 'demo'} owned={ctaTone === 'owned'} className="md:col-span-2">
            <input value={ctaLabel} onChange={(e) => setCta(e.target.value)} className={fieldInputClass(ctaTone)} />
          </Field>
        </div>
      </BeatRow>

      <BeatRow id="beat-line" title="The line on the truck" cue={CUES.truck} showCue={showCues} status={truckStatus}>
        <Field label="Headline" demo={headlineTone === 'demo'} owned={headlineTone === 'owned'}>
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} className={fieldInputClass(headlineTone)} />
        </Field>
      </BeatRow>

      <details
        id="beat-proof"
        open={proofOpen}
        onToggle={(e) => setProofOpen((e.target as HTMLDetailsElement).open)}
        className={`fp-row scroll-mt-24 ${beatCardClass(proofStatus.state)}`}
      >
        <summary className="cursor-pointer">
          <span className="fp-label">Optional proof</span>
          <StatusChip status={proofStatus} />
        </summary>
        {showCues ? <p className="mt-2 text-sm italic text-nm-muted">{CUES.proof}</p> : null}
        <div className="mt-3 grid gap-3">
          <Field label="Proof chips" demo={chipsTone === 'demo'} owned={chipsTone === 'owned'} hint="Comma-separated">
            <input
              value={chips}
              onChange={(e) => setChips(e.target.value)}
              className={fieldInputClass(chipsTone)}
            />
          </Field>
          <Field label="A line they said" demo={quoteTone === 'demo'} owned={quoteTone === 'owned'}>
            <textarea
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              rows={2}
              className={fieldInputClass(quoteTone)}
            />
          </Field>
          <Field label="Who said it">
            <input
              value={quoteName}
              onChange={(e) => setQuoteName(e.target.value)}
              className="fp-input"
            />
          </Field>
        </div>
      </details>

      <details
        open={lookOpen}
        onToggle={(e) => setLookOpen((e.target as HTMLDetailsElement).open)}
        className="fp-card"
      >
        <summary className="cursor-pointer">
          <span className="fp-label">Look</span>
          <span className="ml-2 font-display text-sm font-semibold text-nm-ink">{look.label}</span>
        </summary>
        <p className="mt-2 text-sm text-nm-muted">{crumbs}</p>
        <div className="mt-4">
          <div className="fp-label mb-2">Preset — three looks made for this industry</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset, i) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => selectPreset(i)}
                title={preset.blurb}
                className={`fp-chip ${i === activePresetIndex ? 'fp-chip-on' : ''}`}
              >
                {preset.name}
              </button>
            ))}
          </div>
          {presets[activePresetIndex] ? (
            <p className="mt-2 text-xs text-nm-muted">{presets[activePresetIndex].blurb}</p>
          ) : null}
        </div>
        <div className="mt-4">
          <div className="fp-label mb-2">Loudness — turns this preset down or up, never swaps it</div>
          <div className="flex flex-wrap gap-2">
            {DENSITIES.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDensity(d)}
                className={`fp-chip ${look.density === d ? 'fp-chip-on' : ''}`}
              >
                {DENSITY_LABELS[d]}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <div className="fp-label mb-2">Package</div>
          <div className="flex flex-wrap gap-2">
            {PACKAGE_OPTIONS.map((opt) => (
              <button
                key={opt.pageCount}
                type="button"
                onClick={() => void applyPackage(opt.pageCount)}
                className={`fp-chip ${pageCount === opt.pageCount ? 'fp-chip-on' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <div className="fp-label mb-2 flex items-center justify-between gap-2">
            <span>Character — pick up to {MAX_CHARACTERS} (one or none is fine)</span>
            <span className="text-nm-muted normal-case">{characters.length} of {MAX_CHARACTERS}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {CHARACTERS.map((c) => {
              const on = characters.includes(c);
              const atCap = !on && characters.length >= MAX_CHARACTERS;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCharacter(c)}
                  aria-pressed={on}
                  aria-disabled={atCap}
                  className={`fp-chip ${on ? 'fp-chip-on' : ''} ${atCap ? 'cursor-not-allowed opacity-40' : ''}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-nm-muted">
            Each chip tilts this industry's look — warmth, formality, era — without changing the preset.
          </p>
        </div>
        <label className="mt-4 block text-xs text-nm-muted">
          Accent
          <input
            type="color"
            value={accent}
            onChange={(e) => onAccentChange(e.target.value)}
            className="ml-2 h-8 w-12 cursor-pointer bg-transparent"
          />
        </label>
        {accentIssue ? (
          <p className="mt-2 text-xs text-nm-danger">{accentIssue}</p>
        ) : paletteIssues.length > 0 ? (
          <p className="mt-2 text-xs text-nm-danger">{paletteIssues[0]}</p>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span
            className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold"
            style={{ backgroundColor: accent, color: onAccentColor }}
          >
            Button text preview
          </span>
          <span className="font-mono text-[11px] text-nm-muted">
            {onAccentColor} on {accent} · {accentContrast.toFixed(1)}:1
          </span>
        </div>
      </details>

      <details
        id="beat-photos"
        open={photosOpen}
        onToggle={(e) => setPhotosOpen((e.target as HTMLDetailsElement).open)}
        className={`fp-card scroll-mt-24 ${beatCardClass(photosStatus.state)}`}
      >
        <summary className="cursor-pointer">
          <span className="fp-label">Photos if they send them</span>
          {photoCount > 0 ? <StatusChip status={photosStatus} /> : null}
        </summary>
        <p className="mt-2 text-xs text-nm-muted">
          Skip this on most calls. A team shot can go in Owner (one face) or Project shots (several). Empty slots keep
          monograms and initials.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <AssetSlot
            label="Logo"
            hint="Header. Monogram if empty."
            asset={assets.logo}
            busy={assetBusy === 'logo'}
            ready={Boolean(assets.logo)}
            onPick={(f) => void uploadSlot('logo', f)}
            onClear={() => void clearSlot('logo')}
          />
          <AssetSlot
            label="Owner / face"
            hint="About page"
            asset={assets.owner}
            busy={assetBusy === 'owner'}
            ready={Boolean(assets.owner)}
            onPick={(f) => void uploadSlot('owner', f)}
            onClear={() => void clearSlot('owner')}
          />
          <AssetSlot
            label="Hero / a job in progress"
            hint="Home hero"
            asset={assets.hero}
            busy={assetBusy === 'hero'}
            ready={Boolean(assets.hero)}
            onPick={(f) => void uploadSlot('hero', f)}
            onClear={() => void clearSlot('hero')}
          />
          <GallerySlot
            items={assets.gallery ?? []}
            busy={assetBusy === 'gallery'}
            onPick={(f) => void uploadSlot('gallery', f)}
            onRemove={(i) => void removeGalleryAt(i)}
          />
        </div>
      </details>

      {err && (
        <div className="rounded-[12px] border border-nm-danger/30 bg-nm-danger-bg px-4 py-3 text-sm text-nm-danger">{err}</div>
      )}

      {(running || streamLog.length > 0) && (
        <div className="fp-card font-mono text-xs">
          {streamLog.map((ev, i) => (
            <ProvisionStreamLine key={`${ev.step}-${i}`} ev={ev} />
          ))}
          {running && streamLog.length === 0 && <div className="animate-pulse text-nm-muted">Starting…</div>}
        </div>
      )}

      <div className="fixed inset-x-0 bottom-14 z-20 px-3 sm:bottom-4 sm:px-6">
        <div className="fp-card mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 !bg-nm-chrome">
          <div className="min-w-0">
            <PreviewLink hostname={lead.provisioning.hostname} cacheBust={previewCacheBust} />
            <DeployPreviewStatus
              streamLog={streamLog}
              running={running}
              lastErr={err}
              provisioningSteps={lead.provisioning.steps}
            />
            <p className={`mt-1 fp-label ${gaps.length === 0 ? 'text-nm-success' : 'text-nm-warn'}`}>{gapLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="fp-label">
              {draft.saving ? 'Saving…' : draft.error ? 'Save failed' : 'Saved'}
            </span>
            <button type="button" disabled={running} onClick={() => void fillDemo()} className="fp-btn fp-btn-ghost">
              Fill demo content
            </button>
            {locked ? (
              <span className="fp-label text-nm-warn">Locked — live project</span>
            ) : (
              <>
                {lead.provisioning.repo ? (
                  <LockLiveControl
                    leadId={lead.id}
                    repo={lead.provisioning.repo}
                    onLocked={async () => {
                      await updateLeadStatus(lead.id, 'In Pipeline');
                      logHistory({
                        leadId: lead.id,
                        user,
                        kind: 'status_change',
                        payload: { from: lead.leadStatus, to: 'In Pipeline', via: 'lock_live' },
                      });
                    }}
                  />
                ) : null}
                <button type="button" disabled={!canGenerate} onClick={() => void generate()} className="fp-btn fp-btn-primary">
                  {running ? 'Generating…' : 'Generate preview'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function toneFor(value: string | undefined, isDemo: boolean, optional = false): FieldTone {
  if (!value?.trim()) return optional ? 'quiet' : 'empty';
  if (isDemo) return 'demo';
  return 'owned';
}

function StatusChip({ status }: { status: BeatStatus }) {
  const cls =
    status.state === 'done'
      ? 'bg-nm-success-bg text-nm-success'
      : status.state === 'demo'
        ? 'bg-nm-warn-bg text-nm-warn'
        : 'bg-nm-surface text-nm-muted';
  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide ${cls}`}>
      {status.state === 'done' ? `✓ ${status.label}` : status.label}
    </span>
  );
}

function BeatRow({
  id,
  title,
  cue,
  showCue,
  status,
  actions,
  children,
}: {
  id: string;
  title: string;
  cue?: string;
  showCue?: boolean;
  status: BeatStatus;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`fp-row scroll-mt-24 ${beatCardClass(status.state)}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="fp-label">{title}</span>
            <StatusChip status={status} />
          </div>
          {showCue && cue && status.state !== 'done' ? (
            <p className="mt-1 text-sm italic text-nm-muted">{cue}</p>
          ) : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function LockLiveControl({
  leadId,
  repo,
  onLocked,
}: {
  leadId: string;
  repo: string;
  onLocked: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      if (busy) return;
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, busy]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await lockLeadCode(leadId);
      await onLocked();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button type="button" disabled={busy} onClick={() => { setError(null); setOpen((v) => !v); }} className="fp-btn fp-btn-ghost">
        Lock as live project
      </button>
      {open && (
        <div
          role="dialog"
          aria-labelledby={titleId}
          className="fp-card absolute bottom-full right-0 z-30 mb-2 w-72 !p-3 shadow-lg"
        >
          <p id={titleId} className="font-display text-sm font-semibold text-nm-ink">
            Lock as live project
          </p>
          <p className="mt-1.5 text-xs text-nm-muted">
            Flashpoint will stop Generate, Edit files, and all pushes to this GitHub repo. There is no undo in the app.
          </p>
          <p className="mt-1.5 font-mono text-[10px] text-nm-tertiary">{repo}</p>
          {error && (
            <p className="mt-2 text-xs text-nm-danger" role="alert">
              {error}
            </p>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button type="button" disabled={busy} onClick={() => setOpen(false)} className="fp-btn fp-btn-ghost !px-3 !py-1.5">
              Cancel
            </button>
            <button type="button" disabled={busy} onClick={() => void confirm()} className="fp-btn fp-btn-success !px-3 !py-1.5">
              {busy ? 'Locking…' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
  demo,
  owned,
  required,
  hint,
  className,
}: {
  label: string;
  children: React.ReactNode;
  demo?: boolean;
  owned?: boolean;
  required?: boolean;
  hint?: string;
  className?: string;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1 flex items-center gap-2 fp-label">
        {label}
        {required ? <span className="text-nm-accent">*</span> : null}
        {owned ? (
          <span aria-hidden className="text-nm-success">
            ✓
          </span>
        ) : null}
        {demo ? (
          <span className="rounded-full bg-nm-warn-bg px-2 py-0.5 text-[9px] font-semibold text-nm-warn">
            Demo fill
          </span>
        ) : null}
        {hint ? <span className="normal-case tracking-normal text-nm-tertiary">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}
